import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import { REPORT_POLL_INTERVAL_MS } from '@/api/consts'
import {
  generateReport,
  getAssessmentReportTemplates,
  listAssessmentReports,
  resetAssessmentReportTemplateToDefault,
  setAssessmentReportTemplate,
  updateAssessmentReport,
} from '@/gen/inventory/v1/assessment_report_service-AssessmentReportService_connectquery'
import {
  type AssessmentReport,
  AssessmentReportService,
} from '@/gen/inventory/v1/assessment_report_service_pb'

import { defaultTransport } from '@/lib/connect/connect-transport'
import { toMs } from '@/lib/date/date'

/**
 * GenerateReport hands back the report as it stood *before* the re-generate, because
 * generation itself runs off the request path. A newer updatedAt on that same row is
 * therefore the signal that the freshly generated content has landed.
 */
export const findRegeneratedReport = (
  reports: AssessmentReport[],
  baseline?: AssessmentReport,
): AssessmentReport | undefined => {
  const candidate = baseline ? reports.find((report) => report.id === baseline.id) : reports[0]
  if (!candidate || toMs(candidate.updatedAt) <= toMs(baseline?.updatedAt)) return undefined
  return candidate
}

export const getAssessmentReportTemplatesQueryKey = () => {
  return createConnectQueryKey({
    schema: AssessmentReportService.method.getAssessmentReportTemplates,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useGetAssessmentReportTemplates = () => {
  const queryState = useQuery(
    getAssessmentReportTemplates,
    {},
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  )

  return {
    ...queryState,
    template: queryState.data?.template,
  }
}

export const useResetAssessmentReportTemplateToDefault = () => {
  const queryClient = useQueryClient()

  return useMutation(resetAssessmentReportTemplateToDefault, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getAssessmentReportTemplatesQueryKey(),
      })
    },
  })
}

export const useSetAssessmentReportTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation(setAssessmentReportTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getAssessmentReportTemplatesQueryKey(),
      })
    },
  })
}

export const getListAssessmentReportsQueryKey = (ongoingAssessmentId?: string) => {
  return createConnectQueryKey({
    schema: AssessmentReportService.method.listAssessmentReports,
    cardinality: 'finite',
    transport: defaultTransport,
    input: { ongoingAssessmentId },
  })
}

/**
 * Keep polling while the initial generation hasn't produced content yet, and — when a
 * re-generate is pending — until the baseline row's updatedAt moves. The re-generate case
 * needs its own check because the previous content is still present throughout, so the
 * content check alone would report completion immediately.
 */
export const reportsRefetchInterval = (
  reports: AssessmentReport[],
  pendingRegeneration?: { baseline?: AssessmentReport },
): number | false => {
  const reportContent = reports[0]?.content ? new TextDecoder().decode(reports[0].content) : ''
  if (reports.length === 0 || reportContent === '') return REPORT_POLL_INTERVAL_MS
  if (pendingRegeneration && !findRegeneratedReport(reports, pendingRegeneration.baseline)) {
    return REPORT_POLL_INTERVAL_MS
  }
  return false
}

/**
 * Pass `pendingRegeneration` while a re-generate is in flight so polling continues until
 * the new content lands. It wraps the baseline because "no report existed before the
 * re-generate" is itself a valid pending state — GenerateReport returns no row in that case.
 */
export const useListAssessmentReports = (
  ongoingAssessmentId?: string,
  pendingRegeneration?: { baseline?: AssessmentReport },
) => {
  const queryState = useQuery(
    listAssessmentReports,
    { ongoingAssessmentId },
    {
      enabled: !!ongoingAssessmentId,
      refetchInterval: (query) =>
        reportsRefetchInterval(query.state.data?.reports || [], pendingRegeneration),
    },
  )

  const reports = queryState.data?.reports || []
  const reportContent = reports[0]?.content ? new TextDecoder().decode(reports[0].content) : ''

  return {
    ...queryState,
    reports,
    reportContent,
    // Set only once the pending re-generate's content has landed, so callers can react
    // to completion without re-deriving the baseline comparison.
    regeneratedReport: pendingRegeneration
      ? findRegeneratedReport(reports, pendingRegeneration.baseline)
      : undefined,
  }
}

export const useUpdateAssessmentReport = (ongoingAssessmentId?: string) => {
  const queryClient = useQueryClient()

  return useMutation(updateAssessmentReport, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListAssessmentReportsQueryKey(ongoingAssessmentId),
      })
    },
  })
}

export const useGenerateReport = (ongoingAssessmentId?: string) => {
  const queryClient = useQueryClient()

  return useMutation(generateReport, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListAssessmentReportsQueryKey(ongoingAssessmentId),
      })
    },
  })
}

export const getGenerateReportQueryKey = (ongoingAssessmentId?: string) => {
  return createConnectQueryKey({
    schema: AssessmentReportService.method.generateReport,
    cardinality: 'finite',
    transport: defaultTransport,
    input: { ongoingAssessmentId },
  })
}
